import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Breadcrumbs,
  Typography,
  Link,
  Button,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  Box,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import TextField from '@components/TextFieldCaseSetting';
import { useDispatch } from 'react-redux';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import {
  fetchPortalNotificationEmailConfig,
  updatePortalNotificationEmailConfig,
  fetchPortalNotificationAgentEmailConfig,
  updatePortalNotificationAgentEmailConfig,
  fetchPortalAgentEmailPreview,
  fetchPortalNotificationPreviewEmail,
} from '../../actions/global';
import RichTextEditor from 'react-rte';
import styled from 'styled-components';
import { showSuccess, showError } from '@actions/global';
import { EditorState, Modifier as Draft } from 'draft-js';

const ConfigureEmailPage = ({ setConfigureEmail }) => {
  const dispatch = useDispatch();

  const [loaded, setLoaded] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState(RichTextEditor.createEmptyValue());
  const [previewPID, setPreviewPID] = useState('');
  const [emailPreview, setEmailPreview] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [agentEmailConfig, setAgentEmailConfig] = useState({
    electronicConfigurationPortalAgentNotificationEmail: RichTextEditor.createEmptyValue(),
    electronicConfigurationPortalAgentNotificationSubject: ''
  });

  useEffect(() => {
    initializeData();
  }, []);

  const initializeData = async () => {
    const response = await dispatch(
      fetchPortalNotificationEmailConfig(),
    );
    if(response?.results?.[0]) {
      setSubject(response.results[0].electronicConfigurationPortalNotificationSubject);
      setBody(RichTextEditor.createValueFromString(response.results[0].electronicConfigurationPortalNotificationEmail, 'html'));
    }
    setLoaded(true);
  };

  const loadAgentEmailConfig = async () => {
    try {
      const response = await dispatch(fetchPortalNotificationAgentEmailConfig());
      if (response?.results?.[0]) {
        setAgentEmailConfig({
          electronicConfigurationPortalAgentNotificationEmail: RichTextEditor.createValueFromString(
            response.results[0].electronicConfigurationPortalAgentNotificationEmail || '', 
            'html'
          ),
          electronicConfigurationPortalAgentNotificationSubject: response.results[0].electronicConfigurationPortalAgentNotificationSubject || ''
        });
      } else {
        // Initialize with empty values if no data is returned
        setAgentEmailConfig({
          electronicConfigurationPortalAgentNotificationEmail: RichTextEditor.createEmptyValue(),
          electronicConfigurationPortalAgentNotificationSubject: ''
        });
      }
    } catch (error) {
      dispatch(showError('Failed to load agent email configuration'));
    }
  };

  const saveAgentEmailConfig = async () => {
    try {
      const response = await dispatch(
        updatePortalNotificationAgentEmailConfig({
          payload: {
            electronicConfigurationPortalAgentNotificationEmail: agentEmailConfig.electronicConfigurationPortalAgentNotificationEmail.toString('html'),
            electronicConfigurationPortalAgentNotificationSubject: agentEmailConfig.electronicConfigurationPortalAgentNotificationSubject
          }
        })
      );
      if (response?.results) {
        dispatch(showSuccess('Agent email configuration saved successfully'));
      } else {
        dispatch(showError('Failed to save agent email configuration'));
      }
    } catch (error) {
      dispatch(showError('Failed to save agent email configuration'));
    }
  };

  const insertAtCursor = (editorValue, insertString) => {
    // Get the current editor state
    const editorState = editorValue.getEditorState();
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    
    // Insert the text at current selection
    const newContentState = Draft.insertText(
      contentState,
      selection,
      insertString
    );
    
    // Create new editor state
    const newEditorState = EditorState.push(
      editorState,
      newContentState,
      'insert-characters'
    );
    
    // Create new RichTextEditor value
    return editorValue.setEditorState(newEditorState);
  };

  const handleAddPID = () => {
    const currentStringValue = body.toString('html');
    if(currentStringValue && currentStringValue !== '<p><br></p>') {
      setBody(insertAtCursor(body, '{pID}'));
    } else {
      setBody(RichTextEditor.createValueFromString(`{pID}`, 'html'));
    }
  };

  const handleAddSitus = () => {
    const currentStringValue = body.toString('html');
    if(currentStringValue && currentStringValue !== '<p><br></p>') {
      setBody(insertAtCursor(body, '{situsAddress}'));
    } else {
      setBody(RichTextEditor.createValueFromString(`{situsAddress}`, 'html'));
    }
  };

  const handleAddLegalDescription = () => {
    const currentStringValue = body.toString('html');
    if(currentStringValue && currentStringValue !== '<p><br></p>') {
      setBody(insertAtCursor(body, '{legalDescription}'));
    } else {
      setBody(RichTextEditor.createValueFromString(`{legalDescription}`, 'html'));
    }
  };

  const handleSave = async () => {
    await dispatch(
      updatePortalNotificationEmailConfig({
        payload: {
          electronicConfigurationPortalNotificationSubject: subject,
          electronicConfigurationPortalNotificationEmail: body.toString('html'),
        }
      }),
    );
    dispatch(showSuccess('Config updated successfully'));
  };

  const handlePreviewEmail = async () => {
    await dispatch(
      updatePortalNotificationEmailConfig({
        payload: {
          electronicConfigurationPortalNotificationSubject: subject,
          electronicConfigurationPortalNotificationEmail: body.toString('html'),
        }
      }),
    );
    const previewData = await dispatch(
      fetchPortalNotificationPreviewEmail({
        notificationId: previewPID,
      }),
    );
    if(previewData?.results) {
      setEmailPreview(previewData.results);
    } else {
      dispatch(showError('Error fetching email preview'));
    }
  };

  const handlePreviewAgentEmail = async () => {
    // First save the current changes
    await dispatch(
      updatePortalNotificationAgentEmailConfig({
        payload: {
          electronicConfigurationPortalAgentNotificationEmail: agentEmailConfig.electronicConfigurationPortalAgentNotificationEmail.toString('html'),
          electronicConfigurationPortalAgentNotificationSubject: agentEmailConfig.electronicConfigurationPortalAgentNotificationSubject
        }
      })
    );

    // Then fetch the preview using the agent preview endpoint
    const previewData = await dispatch(fetchPortalAgentEmailPreview());
    if(previewData?.results) {
      setEmailPreview(previewData.results);
    } else {
      dispatch(showError('Error fetching email preview'));
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    
    if (newValue === 1) {
      // Agent Email Configuration tab
      loadAgentEmailConfig();
    }
  };

  return (
    <Container>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
      >
        <Link
          color="inherit"
          onClick={() => {
            setConfigureEmail(false);
          }}
        >
          Electronic Delivery Requests - Portal Notifications
        </Link>
        <Typography color="textPrimary">
          {'Configure Email'}
        </Typography>
      </Breadcrumbs>

      {!loaded ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <div style={{ padding: '20px' }}>
          <Divider
            style={{
              backgroundColor: '#aeb8ca',
              height: 4,
              marginBottom: 20,
              marginTop: 20,
            }}
          />
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Taxpayer Email Configuration" />
            <Tab label="Agent Email Configuration" />
          </Tabs>

          {activeTab === 0 && (
            <Box p={3}>
              <FieldWrapper>
                <label>Email Subject: </label>
                <TextField
                  variant="outlined"
                  fullWidth
                  style={{ maxWidth: 500 }}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </FieldWrapper>
              <FieldWrapper style={{ marginTop: 20, justifyContent: 'space-between' }}>
                <label>Email Body: </label>
                <EditOptionContainer>
                  <EditOption onClick={handleAddPID}>Add PID</EditOption>
                  <EditOption onClick={handleAddSitus}>Add Situs</EditOption>
                  <EditOption onClick={handleAddLegalDescription}>Add Legal Description</EditOption>
                </EditOptionContainer>
              </FieldWrapper>
              <RichTextWrapper>
                <RichTextEditor
                  value={body}
                  onChange={data => setBody(data)}
                />
              </RichTextWrapper>
              <FlexWrapper style={{ justifyContent: 'flex-end' }}>
                <TextField
                  variant="outlined"
                  label="Property ID"
                  fullWidth
                  style={{ width: 150, marginRight: 20 }}
                  value={previewPID}
                  onChange={(e) => {
                    setPreviewPID(e.target.value);
                  }}
                />
                <EditOption onClick={handlePreviewEmail} disabled={!subject || !body || !previewPID} style={{ marginRight: 20 }}>Preview Email</EditOption>
                <Button variant="contained" color="primary" onClick={handleSave}>Save</Button>
              </FlexWrapper>
            </Box>
          )}

          {activeTab === 1 && (
            <Box p={3}>
              <FieldWrapper>
                <label>Email Subject: </label>
                <TextField
                  variant="outlined"
                  fullWidth
                  style={{ maxWidth: 500 }}
                  value={agentEmailConfig.electronicConfigurationPortalAgentNotificationSubject}
                  onChange={(e) => setAgentEmailConfig({
                    ...agentEmailConfig,
                    electronicConfigurationPortalAgentNotificationSubject: e.target.value
                  })}
                />
              </FieldWrapper>

              <FieldWrapper style={{ marginTop: 20 }}>
                <label>Email Body: </label>
              </FieldWrapper>

              <RichTextWrapper>
                <RichTextEditor
                  value={agentEmailConfig.electronicConfigurationPortalAgentNotificationEmail}
                  onChange={(value) => setAgentEmailConfig({
                    ...agentEmailConfig,
                    electronicConfigurationPortalAgentNotificationEmail: value
                  })}
                />
              </RichTextWrapper>

              <FlexWrapper style={{ justifyContent: 'flex-end', marginTop: 20 }}>
                <EditOption 
                  onClick={handlePreviewAgentEmail}
                  style={{ marginRight: 20 }}
                >
                  Preview Email
                </EditOption>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={saveAgentEmailConfig}
                >
                  Save
                </Button>
              </FlexWrapper>
            </Box>
          )}
        </div>
      )}
      <Dialog
        open={emailPreview}
        onClose={() => setEmailPreview(null)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <IconButton
            aria-label="close"
            onClick={() => setEmailPreview(null)}
            style={{ position: 'absolute', right: 0, top: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div
            style={{ wordBreak: 'break-word' }}
            dangerouslySetInnerHTML={{ __html: emailPreview }}
          />
        </DialogContent>
      </Dialog>
    </Container>
  );
};

ConfigureEmailPage.propTypes = {
  setConfigureEmail: PropTypes.func.isRequired,
};

const Container = styled.div`
  padding: 20px;
  background: #fff;
`;

const FlexWrapper = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const FieldWrapper = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;

  label {
    margin-bottom: 8px;
    white-space: nowrap;
  }
`;

const RichTextWrapper = styled.div`
  margin: 10px 0;
  position: relative;
`;

const EditOptionContainer = styled.div`
  justify-content: flex-end;
  display: flex;
  gap: 20px;
`;

const EditOption = styled.div`
  text-decoration: underline;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  color: ${props => (props.disabled ? '#b0b0b0' : '#0000ee')};
`;

export default ConfigureEmailPage;
